import { SOURCES } from './sources';

export const GUIDES = [
  {
    slug: "saadan-fryser-du-mad",
    title: "Sådan fryser du mad rigtigt",
    metaTitle: "Sådan fryser du mad rigtigt — guide til frysning",
    metaDescription: "Frys maden ved minimum -18 grader, pak lufttæt og mærk med dato. Læs Forbrugerrådet Tænks og Fødevarestyrelsens råd om indfrysning.",
    answer: "Indstil fryseren på minimum -18 °C, pak maden tæt uden luftlommer, og nedkøl tilberedte retter helt, før de lægges i fryseren.",
    steps: [
      "Indstil fryseren på minimum -18 °C for at stoppe bakteriers formering ifølge Forbrugerrådet Tænk.",
      "Køl altid varme retter hurtigt ned i køleskabet, før de kommer i fryseren.",
      "Del maden (især hakkekød) i mindre portioner, så den fryser hurtigt og ensartet igennem.",
      "Pres al overskydende luft ud af fryseposen for at mindske iltning og risiko for rimfrostskader.",
      "Fordel maden i fryseren og brug indfrysningsfunktionen ved større portioner."
    ],
    tableNote: "Kilde: Forbrugerrådet Tænk og Fødevarestyrelsen",
    table: [
      { food: "Mager mad (oksekød, grønt)", duration: "6–12 måneder", pack: "Frysepose, luft presset ud", thaw: "Køleskab ved højst 5 °C", use: "Gennemstegning eller gryderetter" },
      { food: "Fed mad (laks, fedt svinekød)", duration: "2–3 måneder", pack: "Vakuum eller tæt frysepose", thaw: "Køleskab på en dyb tallerken", use: "Varmebehandles grundigt" },
      { food: "Færdige retter", duration: "1–3 måneder", pack: "Tætsluttende boks eller pose", thaw: "Direkte i gryde eller køleskab", use: "Varmes grundigt igennem" },
      { food: "Brød og bagværk", duration: "1–3 måneder", pack: "Lukket frysepose", thaw: "Stuetemperatur eller ovn", use: "Spises straks eller lunes" }
    ],
    sections: [
      {
        h2: "Hvorfor fryserens temperatur skal være minimum -18 grader",
        body: `For at opnå sikker opbevaring af maden skal din fryser være minimum -18 °C ifølge Forbrugerrådet Tænk. Bakterier stopper med at formere sig ved -18 °C, hvilket er med til at forlænge madens holdbarhed.

Det anbefales at tjekke temperaturen og afrime fryseren jævnligt. Fryseren fryser maden mere effektivt, når der ikke har samlet sig store islag på siderne.`
      },
      {
        h2: "Hurtig indfrysning bevarer smag og saftighed",
        body: `Når mad fryses ned hurtigt, holder den bedre på både smag og konsistens. Under nedfrysning dannes der iskrystaller i maden, og jo langsommere processen går, desto større bliver krystallerne. Store iskrystaller fører til, at maden mister mere væske ved optøning.

Ved hurtig nedfrysning dannes der mindre iskrystaller, hvilket hjælper med at bevare madens smag, saftighed og struktur. Forbrugerrådet Tænk råder til at fordele maden i skufferne og aktivere fryserens indfrysningsfunktion i forvejen ved større mængder.`
      },
      {
        h2: "Pakning: Fjern luften for at undgå frostskader",
        body: `Emballage med meget luft i er ikke godt for madens kvalitet. Jo mindre luft der er omkring maden, desto mindre bliver iltningen, og risikoen for rimfrostskader på overfladen reduceres.

Tænk anbefaler poser frem for bokse og påpeger, at vakuumpakning er en stor fordel. Pak altid ferskvarer om i rigtige fryseposer. Mad, der skal fryses i længere tid, kan med fordel pakkes i en papirspose eller madpapir og derefter i en frysepose for at beskytte mod frostskader.`
      },
      {
        h2: "Portioner og mærkning med dato",
        body: `Selv en fryser med høj kapacitet kan have svært ved at gennemfryse en stor klump kød ensartet. Derfor giver det bedre kvalitet efter optøning, hvis du deler for eksempel hakkekød i mindre portioner, før du fryser det ned. Mærk altid emballagen med indhold og dato.`
      }
    ],
    faq: [
      { q: "Hvilken temperatur skal en fryser have?", a: "En fryser skal altid være minimum -18 °C ifølge Forbrugerrådet Tænk, da bakterier stopper med at formere sig ved denne temperatur." },
      { q: "Hvad sker der, hvis maden fryser for langsomt?", a: "Langsom nedfrysning danner store iskrystaller, som medfører, at maden mister mere væske ved optøning." },
      { q: "Må man fryse ferskvarer i supermarkedets bakker?", a: "Nej, Forbrugerrådet Tænk anbefaler altid at pakke ferskvarer om i rigtige fryseposer, medmindre maden er købt på frost." },
      { q: "Hvorfor skal man presse luften ud af fryseposen?", a: "Mindre luft omkring maden mindsker iltningen og reducerer risikoen for rimfrostskader på overfladen." }
    ],
    internalLinks: [
      { text: "Læs om holdbarhed i fryseren", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Sådan tør du mad op på en sikker måde", href: "/optoening" },
      { text: "Regler for genfrysning af mad", href: "/kan-man-genfryse" },
      { text: "Oversigt over fødevarer der ikke tåler frost", href: "/hvad-kan-man-ikke-fryse" }
    ],
    sources: [
      SOURCES.taenkNedfrysning,
      SOURCES.foedevarestyrelsenOpbevaring
    ]
  },
  {
    slug: "hvor-laenge-holder-mad-i-fryseren",
    title: "Holdbarhed i fryseren",
    metaTitle: "Hvor længe holder mad i fryseren? — Komplet tabel",
    metaDescription: "Få overblik over holdbarhed på frost for kød, fjerkræ, fisk, grøntsager, brød og færdigretter baseret på Forbrugerrådet Tænks tal.",
    answer: "Oksekød holder ca. 10 måneder, kylling 8–10 måneder, magert svinekød ca. 6 måneder, grøntsager 6–12 måneder og færdigretter 1–3 måneder.",
    steps: [
      "Brug fedtindholdet som rettesnor: Jo mere fedt maden indeholder, desto hurtigere iltes den og forringes i kvalitet.",
      "Sørg for, at fryseren holder en konstant temperatur på minimum -18 °C.",
      "Pak maden helt lufttæt i fryseposer for at reducere iltning og rimfrostskader.",
      "Skriv altid indfrysningsdato på emballagen.",
      "Gennemgå fryserens indhold regelmæssigt og brug de ældste varer først."
    ],
    tableNote: "Kilde: Forbrugerrådet Tænk og Arla",
    table: [
      { food: "Oksekød og lammekød", duration: "Cirka 10 måneder", pack: "Frysepose uden luft", thaw: "Køleskab", use: "Gennemstegning eller simreretter jf. Tænk" },
      { food: "Kylling", duration: "8–10 måneder", pack: "Frysepose eller vakuum", thaw: "Køleskab i skål", use: "Gennemsteges til mindst 75 °C jf. Arla" },
      { food: "Magert svinekød", duration: "Cirka 6 måneder", pack: "Tætsluttende frysepose", thaw: "Køleskab", use: "Stegning jf. Tænk" },
      { food: "Hakket kød", duration: "Cirka 4 måneder", pack: "Fladtrykt i frysepose", thaw: "Køleskab", use: "Gennemsteges altid; fars holder 2 mdr. jf. Tænk" },
      { food: "Fedt svinekød", duration: "Cirka 3 måneder", pack: "Frysepose", thaw: "Køleskab", use: "Fedtet harsker hurtigere jf. Tænk" },
      { food: "Pølser", duration: "2–3 måneder", pack: "Frysepose", thaw: "Køleskab", use: "Gennemvarmning jf. Tænk" },
      { food: "Røget kød", duration: "4–6 måneder", pack: "Frysepose", thaw: "Køleskab", use: "Kogning eller stegning jf. Tænk" },
      { food: "Mager fisk", duration: "3–6 måneder", pack: "Tætsluttende pose", thaw: "Køleskab", use: "Kogning eller bagning jf. Tænk" },
      { food: "Fed fisk (laks, ørred, sild)", duration: "2–3 måneder", pack: "Vakuum eller frysepose", thaw: "Køleskab", use: "Gennemstegning jf. Tænk" },
      { food: "Fiskefars", duration: "1–2 måneder", pack: "Frysepose", thaw: "Køleskab", use: "Gennemstegning jf. Tænk" },
      { food: "Røget fisk", duration: "Cirka 3 uger", pack: "Tæt lukket", thaw: "Køleskab", use: "Kort holdbarhed på frost jf. Tænk" },
      { food: "Grøntsager", duration: "6–12 måneder", pack: "Frysepose", thaw: "Direkte i varm ret", use: "Afhængig af emballage og type jf. Tænk" },
      { food: "Frugt og frugtprodukter", duration: "10–12 måneder", pack: "Frysepose eller boks", thaw: "Køleskab eller frossen", use: "Grød, kompot eller bagværk jf. Tænk" },
      { food: "Færdige retter", duration: "1–3 måneder", pack: "Fryseegnet boks", thaw: "Køleskab eller gryde", use: "Varmes grundigt igennem jf. Tænk" },
      { food: "Smør", duration: "Op til 6 måneder", pack: "Original folie + pose", thaw: "Køleskab", use: "Bevarer smag og kvalitet flot jf. Arla" },
      { food: "Skæreost over 45+", duration: "3–4 måneder", pack: "Folie/madpapir og pose", thaw: "Køleskab", use: "Smuldrer; god til gratinering jf. Arla" }
    ],
    sections: [
      {
        h2: "Hvorfor bliver mad for gammel i en fryser?",
        body: `Forbrugerrådet Tænk forklarer, at bakterievækst stopper ved -10 °C, og at man ikke bliver syg af at spise mad, der har ligget længere end den anbefalede tid i fryseren. Produkterne bliver blot kvalitetsmæssigt forringet.

Konsistens, duft og smag ændrer sig over tid: Fedtstoffer reagerer langsomt med ilt og harsker, enzymer nedbryder cellevægge, og maden kan udtørre af fryseluften, hvis den ikke er pakket tæt ind.`
      },
      {
        h2: "Fedtindholdet bestemmer holdbarheden",
        body: `Ifølge Forbrugerrådet Tænks holdbarhedstabel er fedtindholdet afgørende: Mager okse og lam holder cirka 10 måneder, magert svinekød cirka 6 måneder, mens fedt svinekød kun holder cirka 3 måneder.

Kylling kan holde sig 8–10 måneder. For fisk gælder, at mager fisk holder 3–6 måneder, mens fed fisk som laks holder 2–3 måneder og røget fisk blot cirka 3 uger.`
      },
      {
        h2: "Kvalitet frem for sundhedsfare",
        body: `Har en vare ligget længere i fryseren end de vejledende tider, er den altså ikke sundhedsfarlig, så længe fryseren har været kold nok. Men maden kan have mistet saftighed og smag. Tilbered maden og vurdér den med sanserne: Lugt og smag på den, og kassér den, hvis smagen er harsk eller uappetitlig.`
      }
    ],
    faq: [
      { q: "Bliver man syg af mad, der har ligget for længe i fryseren?", a: "Nej, Forbrugerrådet Tænk understreger, at man ikke bliver syg, da bakterievækst stopper, men madens kvalitet forringes." },
      { q: "Hvor længe kan kylling holde i fryseren?", a: "Kylling holder 8–10 måneder i fryseren ifølge Forbrugerrådet Tænk." },
      { q: "Hvor længe holder hakket kød i forhold til farskød?", a: "Hakket kød holder cirka 4 måneder, mens farskød med tilsat væske kun holder cirka 2 måneder jf. Tænk." },
      { q: "Hvor længe holder grøntsager og frugt på frost?", a: "Grøntsager holder 6–12 måneder, og frugt holder 10–12 måneder ifølge Forbrugerrådet Tænk." }
    ],
    internalLinks: [
      { text: "Læs den grundlæggende guide til frysning", href: "/saadan-fryser-du-mad" },
      { text: "Sådan tør du kød og fisk op", href: "/optoening" },
      { text: "Holdbarhed for kød og fjerkræ på frost", href: "/koed" },
      { text: "Holdbarhed for fisk og skaldyr", href: "/fisk-og-skaldyr" }
    ],
    sources: [
      SOURCES.taenkHoldbarhed,
      SOURCES.taenkNedfrysning,
      SOURCES.arlaSmoer
    ]
  },
  {
    slug: "optoening",
    title: "Optøning af mad",
    metaTitle: "Optøning af mad: Regler og metoder — Kanmanfryse.dk",
    metaDescription: "Tø altid kød, fisk og madretter op i køleskabet for at hæmme bakterievækst jf. Fødevarestyrelsen og Tænk. Læs om vandbad og mikroovn.",
    answer: "Tø altid frossen mad op i køleskabet i en skål eller et fad; kulden hæmmer bakterievækst og mindsker væsketab.",
    steps: [
      "Fødevarestyrelsen råder til at lægge frossent kød, fisk og madretter til optøning i en plastikboks, høj skål eller et fad i køleskabet.",
      "Sørg for, at fadet er stort nok, så væske ikke drypper ned på andre madvarer.",
      "Optøning i køleskabet hæmmer bakterievækst, mindsker væsketab og sparer energi i køleskabet ifølge Fødevarestyrelsen.",
      "Ved lynoptøning i vandbad eller mikroovn skal maden varmebehandles grundigt eller spises hurtigst muligt efter optøning.",
      "Læg aldrig frossent kød til optøning på køkkenbordet ved stuetemperatur."
    ],
    tableNote: "Kilde: Fødevarestyrelsen og Forbrugerrådet Tænk",
    table: [
      { food: "Køleskab (anbefalet)", duration: "12–24 timer pr. kg", pack: "Høj skål, boks eller fad", thaw: "Langsom ved højst 5 °C", use: "Hæmmer bakterievækst og bevarer kvaliteten bedst jf. Fødevarestyrelsen" },
      { food: "Koldt vandbad (hastværk)", duration: "Hurtigere end køkkenbord", pack: "Helt tæt lukket frysepose", thaw: "Skål med koldt vand", use: "Maden skal varmebehandles grundigt eller spises straks jf. Fødevarestyrelsen" },
      { food: "Mikrobølgeovn", duration: "Få minutter", pack: "Uden al emballage på tallerken", thaw: "Optøningsprogram (defrost)", use: "Uens optøning; skal tilberedes straks jf. Tænk" },
      { food: "Køkkenbord (frarådes)", duration: "Risikabel", pack: "Ikke anbefalet", thaw: "Stuetemperatur", use: "Overfladen når temperaturer med risiko for bakterievækst" }
    ],
    sections: [
      {
        h2: "Fødevarestyrelsens fordele ved optøning i køleskabet",
        body: `Fødevarestyrelsen fremhæver tre klare fordele ved at optø frossent kød, fisk, færdigretter og andre madvarer i køleskabet:
1. Kulden i køleskabet hæmmer vækst af bakterier i madvarerne.
2. Maden mister mindre væske og bevarer kvaliteten bedre.
3. Du sparer lidt på køleskabets energiforbrug.

Læg altid maden i en skål, plastikboks eller et fad, der er tilstrækkeligt stort, så saft eller væske ikke løber ud i køleskabet eller drypper ned på andre madvarer.`
      },
      {
        h2: "Hvorfor køkkenbordet frarådes",
        body: `Forbrugerrådet Tænk advarer mod at lægge mad til optøning på køkkenbordet:
Hvis du lægger fødevarer til optøning på køkkenbordet, kan der vokse bakterier frem i løbet af dagen. Det sker, fordi overfladen hurtigt bliver optøet og kan blive så varm, at der dannes grundlag for bakterievækst, selvom kernen stadig er frossen.

Derfor bør især frossent kød, fisk og madrester altid optøs i køleskabet.`
      },
      {
        h2: "Når det skal gå hurtigt: Vandbad og mikroovn",
        body: `Har du ikke tid til at optø maden i køleskabet og må ty til en hurtigere optøning i vandbad eller i mikrobølgeovn, anbefaler Fødevarestyrelsen og Forbrugerrådet Tænk, at du varmebehandler maden grundigt eller spiser den hurtigst muligt efter optøning for at undgå bakterieudvikling.

Ved vandbad lægges maden i en skål med koldt vand i en helt tætlukket pose. Ved mikrobølgeovn skal al emballage fjernes, og maden skal tilberedes straks, da dele af kødet allerede kan begynde at tilberede under afrimningen.`
      }
    ],
    faq: [
      { q: "Hvorfor skal frossen mad optøs i køleskabet?", a: "Fødevarestyrelsen forklarer, at kulden hæmmer bakterievækst, maden mister mindre væske, og du sparer lidt på køleskabets energiforbrug." },
      { q: "Hvorfor må kød ikke tø op på køkkenbordet?", a: "Forbrugerrådet Tænk påpeger, at overfladen hurtigt bliver så varm, at bakterier kan formere sig, mens midten stadig er frossen." },
      { q: "Hvad anbefales, hvis man tør op i vandbad eller mikroovn?", a: "Fødevarestyrelsen anbefaler, at maden varmebehandles grundigt eller spises hurtigst muligt efter optøning." },
      { q: "Hvordan undgår man, at optøningsvæske forurener køleskabet?", a: "Læg altid maden i en rummelig skål eller et fad med høje kanter på den nederste hylde i køleskabet." }
    ],
    internalLinks: [
      { text: "Læs reglerne for genfrysning af optøet mad", href: "/kan-man-genfryse" },
      { text: "Holdbarhedstabeller for kød og fisk", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Frysning og optøning af kød", href: "/koed" },
      { text: "Optøning af flæskesteg", href: "/flaeskesteg" },
      { text: "Optøning af pølser", href: "/poelser" }
    ],
    sources: [
      SOURCES.foedevarestyrelsenOptoening,
      SOURCES.taenkOptoening,
      SOURCES.taenkNedfrysning
    ]
  },
  {
    slug: "kan-man-genfryse",
    title: "Kan man genfryse mad?",
    metaTitle: "Må man genfryse mad? Regler for kød, brød og kage",
    metaDescription: "Forbrugerrådet Tænk og Fødevarestyrelsens regler: Optøet mad i køleskab kan genfryses inden for samme døgn. Læs detaljerne her.",
    answer: "Ja, mad der har været tøet op i køleskabet kan genfryses, så længe det sker inden for samme døgn, men madens kvalitet påvirkes.",
    steps: [
      "Tjek altid varen for producenter, der skriver »Må ikke genindfryses«; i så fald anbefaler Forbrugerrådet Tænk at følge rådet.",
      "Hvis der ikke er angivet noget, kan maden som regel genfryses jf. Tænk.",
      "Kravet er, at det sker inden for samme døgn, og maden har været opbevaret i køleskabet i mellemtiden.",
      "Har maden stået og tøet op på køkkenbordet, skal den først tilberedes eller varmes grundigt igennem jf. Tænk.",
      "Efter grundig gennemvarmning køles maden hurtigt ned, hvorefter den kan komme tilbage i fryseren."
    ],
    tableNote: "Kilde: Forbrugerrådet Tænk og Fødevarestyrelsen",
    table: [
      { food: "Mad optøet i køleskab", duration: "Genfrys inden for samme døgn", pack: "Frysepose", thaw: "Køleskab", use: "Kan genfryses jf. Tænk; kvaliteten påvirkes dog" },
      { food: "Mad optøet på køkkenbord", duration: "Må ikke genfryses rå", pack: "Skal tilberedes straks", thaw: "Varmes grundigt igennem", use: "Skal gennemvarmes, køles hurtigt ned og kan derefter fryses jf. Tænk" },
      { food: "Vare mærket »Må ikke genindfryses«", duration: "Følg producentens anvisning", pack: "Original anvisning", thaw: "Køleskab", use: "Tænk anbefaler at følge producentens mærkning" },
      { food: "Brød og bagværk", duration: "Kan genfryses", pack: "Tætsluttende pose", thaw: "Stuetemperatur", use: "Bliver hurtigere tørt; velegnet til ristning" },
      { food: "Smør og margarine", duration: "Kan genfryses", pack: "Tæt lukket", thaw: "Køleskab", use: "Bevarer kvaliteten fornuftigt" }
    ],
    sections: [
      {
        h2: "Forbrugerrådet Tænks klare regler for genfrysning",
        body: `Forbrugerrådet Tænk opsummerer reglerne for genfrysning meget præcist:
»En del producenter skriver på deres fødevarer, hvis de ikke kan genfryses – står der dette på en vare, anbefaler vi, at du følger producentens råd.

Hvis der ikke er angivet noget om genfrysning på en vare, kan du som regel genfryse den – vær dog opmærksom på, at madens kvalitet påvirkes.

Hvis du har taget mad ud af fryseren, men ender med ikke at bruge det alligevel, kan det meste fryses igen – så længe det sker inden for samme døgn, og maden har været opbevaret i køleskabet i mellemtiden.«`
      },
      {
        h2: "Hvad hvis maden har stået på køkkenbordet?",
        body: `Har maden derimod stået og tøet op på køkkenbordet, gælder der et andet krav:
Ifølge Forbrugerrådet Tænk skal maden først tilberedes eller varmes grundigt igennem og derefter køles hurtigt ned, før den kan komme tilbage i fryseren.

Varmen inaktiverer de bakterier, der måtte have udviklet sig på overfladen ved stuetemperatur. Når retten efterfølgende er kølet hurtigt ned på køl, kan den fryses ned som en tilberedt færdigret.`
      },
      {
        h2: "Kvalitet og væsketab ved genfrysning",
        body: `Når mad genfryses, sker der en gentagelse af krystaldannelsen i cellerne. Det betyder, at kød vil smide ekstra kødsaft ved anden optøning og fremstå mere tørt.

Genfrosset råt kød er derfor mest velegnet til sammenkogte retter, gryderetter eller supper, hvor kødets eventuelle tørhed opvejes af saucen.`
      }
    ],
    faq: [
      { q: "Må man genfryse mad, der har ligget i køleskabet?", a: "Ja, ifølge Forbrugerrådet Tænk kan det meste genfryses, så længe det sker inden for samme døgn, og maden har været opbevaret i køleskabet i mellemtiden." },
      { q: "Hvad skal man gøre, hvis maden har tøet op på køkkenbordet?", a: "Tænk understreger, at maden først skal tilberedes eller varmes grundigt igennem og køles hurtigt ned, før den må fryses igen." },
      { q: "Hvad betyder det, hvis der står »Må ikke genindfryses«?", a: "Forbrugerrådet Tænk anbefaler altid at følge producentens råd, hvis det fremgår af varen." },
      { q: "Bliver mad farlig af at blive genfrosset?", a: "Ikke hvis optøningen er sket koldt i køleskabet inden for samme døgn; det er primært madens kvalitet og saftighed, der forringes." }
    ],
    internalLinks: [
      { text: "Råd om sikker optøning i køleskab", href: "/optoening" },
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Holdbarhed for mad i fryseren", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Frysning og genfrysning af brød", href: "/broed-og-dej" },
      { text: "Kan man genfryse flæskesteg?", href: "/flaeskesteg" },
      { text: "Kan man genfryse pølser?", href: "/poelser" }
    ],
    sources: [
      SOURCES.taenkNedfrysning,
      SOURCES.foedevarestyrelsenOptoening
    ]
  },
  {
    slug: "hvad-kan-man-ikke-fryse",
    title: "Hvad kan man ikke fryse?",
    metaTitle: "Hvad kan man ikke fryse? — Fødevarer der ikke tåler frost",
    metaDescription: "Fødevarer der mister struktur eller skiller på frost: Rå æg i skal, agurk, salat, kartofler og mayonnaise. Se overblikket her.",
    answer: "Hele rå æg i skal, vandholdige grøntsager til rå brug (agurk, salat), emulsioner som mayonnaise/bearnaise samt rå kartofler tåler ikke frysning.",
    steps: [
      "Frys aldrig hele rå æg i skallen; væsken udvider sig og sprænger skallen.",
      "Undgå at fryse grøntsager, der skal spises rå og sprøde (agurk holder ca. 1 uge i køleskab jf. Tænk).",
      "Frys ikke rå kartofler; stivelsen omdannes og gør kartoflen sød og svampet jf. Arla.",
      "Saucer baseret på æg- og olieemulsion (mayonnaise, bearnaise) brydes og skiller til ren olie.",
      "Mejeriprodukter som creme fraiche og yoghurt skiller på frost og kan kun bruges i varme retter eller bagning."
    ],
    tableNote: "Kilde: Fødevarestyrelsen, Forbrugerrådet Tænk og Arla",
    table: [
      { food: "Hele rå æg i skal", duration: "Uegnet til frost (skallen sprænges)", pack: "Væsken udvider sig", thaw: "Skallen krakelerer", use: "Må aldrig fryses i skal; slå ud først" },
      { food: "Agurk og frisk bladsalat", duration: "Uegnet til frost", pack: "Cellerne sprænges af vand", thaw: "Bliver en slimet, vandet masse", use: "Agurk holder ca. 1 uge i køleskab jf. Tænk" },
      { food: "Rå kartofler", duration: "Uegnet til frysning", pack: "Stivelsen nedbrydes jf. Arla", thaw: "Bliver mørke, vandede og søde", use: "Rå kartofler egner sig ikke til frys jf. Arla" },
      { food: "Mayonnaise og bearnaise", duration: "Uegnet til frysning", pack: "Emulsionen brydes uopretteligt", thaw: "Skiller til klar olie og bundfald", use: "Kan ikke piskes sammen igen" },
      { food: "Creme fraiche og yoghurt (til kold brug)", duration: "Uegnet til kolde dressinger", pack: "Skiller til valle og kaseingryn", thaw: "Grynet konsistens", use: "Kan bruges i varme retter med jævning jf. Arla" }
    ],
    sections: [
      {
        h2: "Hvorfor vandholdige grøntsager ødelægges på frost",
        body: `Når vand fryser til is, udvider det sig. Fødevarer med et meget højt naturligt vandindhold – som agurk og salatblade – består af spændstige planteceller fyldt med vand.

Iskrystallerne sprænger cellevæggene under indfrysningen. Når varen tør op, løber cellevæsken ud som rent vand, og grøntsagen kollapser til en slatten, grødet masse uden bid. Forbrugerrådet Tænk anbefaler i stedet at opbevare agurk i dens originale indpakning i køleskabet, hvor den holder cirka 1 uge.`
      },
      {
        h2: "Hele rå æg og rå kartofler",
        body: `Hele rå æg må aldrig fryses i deres skal, da den udvidende æggemasse uvægerligt sprænger kalkskallen og blotlægger indholdet. Slå altid æggene ud først.

Rå kartofler egner sig ifølge Arlas kartoffelguide slet ikke til frysning, da stivelsen omdannes til sukkerstoffer, hvilket giver en sød, mørk og våd konsistens. Kartofler skal altid tilberedes (fx som mos) inden frysning.`
      },
      {
        h2: "Emulsioner og mejeriprodukter",
        body: `Saucer stabiliseret med æg og fedt som mayonnaise og bearnaise kan ikke tåle frost. Når vandet fryser, brydes emulsionen, og olien udskilles fuldstændigt.

Mejeriprodukter som creme fraiche og yoghurt skiller ligeledes til valle og gryn på frost. De kan ikke bruges til kolde dip eller dressinger, men kan jævnes og bruges i varme gryderetter jf. Arlas anvisninger.`
      }
    ],
    faq: [
      { q: "Hvorfor kan man ikke fryse rå kartofler?", a: "Arla forklarer, at stivelsen i rå kartofler omdannes til sukkerstoffer, så kartoflen bliver sød, mørk og svampet." },
      { q: "Hvad sker der med rå æg i fryseren?", a: "Væsken i ægget udvider sig og sprænger skallen. Æg skal slås ud før frysning." },
      { q: "Kan man fryse creme fraiche?", a: "Den skiller ved optøning og er uegnet til kolde dressinger, men kan koges i varme retter ifølge Arla." },
      { q: "Hvor længe holder en agurk i køleskabet?", a: "Agurk holder cirka 1 uge i sin originale indpakning i køleskabet ifølge Forbrugerrådet Tænk." }
    ],
    internalLinks: [
      { text: "Læs om frysning af mejeriprodukter", href: "/floede" },
      { text: "Frysning af kartofler og mos", href: "/kartofler" },
      { text: "Regler for korrekt indfrysning", href: "/saadan-fryser-du-mad" },
      { text: "Kan man fryse æg?", href: "/aeg" }
    ],
    sources: [
      SOURCES.foedevarestyrelsenOpbevaring,
      SOURCES.taenkHoldbarhed,
      SOURCES.arlaKartofler
    ]
  },
  {
    slug: "fryse-i-glas",
    title: "Kan man fryse i glas og fade?",
    metaTitle: "Kan man fryse i glas og fade? — Undgå sprængt glas",
    metaDescription: "Sådan fryser du i glas og ildfaste fade uden at de revner: Efterlad luft i toppen, brug lige sider og undgå bratte temperaturforskelle.",
    answer: "Ja, man kan godt fryse i glas og ildfaste fade, forudsat at der efterlades mindst 2–3 cm luft i toppen, og glasset ikke udsættes for brat temperaturudsving.",
    steps: [
      "Vælg glas med lige eller koniske sider; glas med indsnævring ved halsen knækker, når indholdet presses opad.",
      "Fyld aldrig glasset mere end 3/4 op, da væsker udvider sig med cirka 9 % under frysning.",
      "Køl altid maden helt ned til køleskabstemperatur, før glasset sættes i fryseren.",
      "Skru først låget helt stramt til, efter indholdet er fuldstændigt gennemfrosset.",
      "Tø altid glasset langsomt op i køleskabet for at forhindre termisk chok."
    ],
    tableNote: "Kilde: Fødevarestyrelsen og Forbrugerrådet Tænk",
    table: [
      { food: "Sylteglas med lige sider (Weck, patent)", duration: "Ubegrænset for selve glasset", pack: "Fyld højst 3/4 fuldt", thaw: "Køleskab langsomt", use: "Pålidelig beholder uden plastkemi" },
      { food: "Glas med indsnævret hals (flasker)", duration: "Høj risiko for sprængning", pack: "Ikke velegnet til flydende mad", thaw: "Køleskab", use: "Indsnævringen blokerer for væskeudvidelsen" },
      { food: "Ildfaste fade (borosilikatglas)", duration: "Tåler frost godt", pack: "Tildækket med låg eller folie", thaw: "Køleskab før ovn", use: "Må aldrig sættes direkte fra fryser i hed ovn" },
      { food: "Plastbøtter (godkendt til frost)", duration: "Fleksibel beholder", pack: "Tætsluttende låg", thaw: "Køleskab eller koldt vand", use: "Plasten giver sig let ved udvidelse" }
    ],
    sections: [
      {
        h2: "Hvorfor revner glas i fryseren?",
        body: `Vand udvider sig fysisk med cirka 9 procent, når det fryser til is. Hvis en flydende suppe, fond eller bærkompot er fyldt helt til kanten i et glas, har isen intet frirum at brede sig på.

Det opbyggede tryk vil uundgåeligt sprænge glasset. Derudover er almindeligt glas følsomt over for termisk spænding: Hældes varm mad i et glas, der sættes i fryseren, kan temperaturforskellen få glasset til at krakelere.`
      },
      {
        h2: "Formen på glasset er afgørende",
        body: `Det er ikke ligegyldigt, hvilket glas du vælger. Glas med fuldstændig lige sider eller en svagt konisk form (hvor åbningen er bredere end bunden) er langt de sikreste at fryse i. Her kan den frysende masse uhindret skydes opad i glasset, efterhånden som den udvider sig.

Glas med "skuldre" eller indsnævrede halse – som mange flasker og syltetøjsglas – revner næsten altid under halsen, fordi den faste isprop kiler sig fast i indsnævringen.`
      },
      {
        h2: "Ildfaste fade: Pas på termisk chok",
        body: `Ildfaste fade af hærdet glas tåler frost glimrende og er ideelle til at fryse lasagne, tærter eller gryderetter ned i.

Den store fare opstår ved genopvarmning. Et koldt fad må ikke sættes direkte fra fryseren ind i en brandvarm ovn på 200 °C. Den bratte temperaturforskel vil give et kraftigt termisk chok, som kan få glasset til at sprænge. Tø altid fadet op i køleskabet først, eller sæt det i en kold ovn, der varmes roligt op.`
      }
    ],
    faq: [
      { q: "Hvor meget luft skal man efterlade i et glas, der skal fryses?", a: "Efterlad altid mindst 2–3 centimeter frirum i toppen (højst 3/4 fuldt), så væsken har plads til at udvide sig." },
      { q: "Må man sætte et frosset ildfast fad direkte i ovnen?", a: "Nej, det kan sprænge glasset pga. termisk chok. Tø det op i køleskabet eller sæt det i en kold ovn, der varmes langsomt op." },
      { q: "Hvilke glas er bedst at fryse ned i?", a: "Glas med lige eller koniske sider uden indsnævring ved halsen (f.eks. cylindriske patentglas eller sylteglas)." },
      { q: "Hvordan tør man bedst mad i glas op?", a: "Stil altid glasset til langsom optøning i køleskabet; hæld aldrig kogende vand over et frosset glas." }
    ],
    internalLinks: [
      { text: "Læs de generelle råd om frysning af mad", href: "/saadan-fryser-du-mad" },
      { text: "Sådan tør du frosne retter sikkert op", href: "/optoening" },
      { text: "Frysning af færdigretter og lasagne", href: "/faerdigretter" },
      { text: "Hvad der ikke tåler frysning", href: "/hvad-kan-man-ikke-fryse" }
    ],
    sources: [
      SOURCES.foedevarestyrelsenOpbevaring,
      SOURCES.taenkNedfrysning
    ]
  }
];
